import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, EnvelopeSimple, Phone, ArrowUpRight } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MaskedLines } from "@/components/Motion";
import { COMPANY, SERVICES } from "@/data/content";
import { submitContact } from "@/lib/api";

const initial = { name: "", email: "", company: "", service: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const err = {};
    if (form.name.trim().length < 2) err.name = "Please enter your name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Enter a valid email";
    if (form.message.trim().length < 5) err.message = "Tell us a little more";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Message sent", { description: "Our team will get back to you shortly." });
      setForm(initial);
      setErrors({});
    } catch (err) {
      toast.error("Something went wrong", { description: "Please try again in a moment." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <section className="bg-[#050a11] text-white pt-40 pb-20 md:pb-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <p className="overline text-[#9dc0ff] mb-8">Contact</p>
          <h1 className="font-display font-light text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.92] tracking-tight">
            <MaskedLines lines={["Johor Bahru,", "Malaysia."]} />
          </h1>
        </div>
      </section>

      <section className="bg-[#f8f9fa] py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid md:grid-cols-12 gap-16">
          {/* details */}
          <div className="md:col-span-5">
            <p className="text-[#4b5563] text-lg leading-relaxed max-w-md">
              Tell us about your sourcing, supply or operations needs — our dedicated team will respond personally.
            </p>
            <div className="mt-12 space-y-8">
              <div className="flex gap-4">
                <MapPin size={24} weight="duotone" className="text-[#0b2046] mt-1 shrink-0" />
                <address className="not-italic text-[#050a11] leading-relaxed">
                  {COMPANY.addressLines.map((l, i) => (
                    <div key={i}>{l}</div>
                  ))}
                </address>
              </div>
              <div className="flex gap-4 items-center">
                <EnvelopeSimple size={24} weight="duotone" className="text-[#0b2046] shrink-0" />
                <a href={`mailto:${COMPANY.email}`} data-testid="contact-email-link" className="text-[#050a11] link-sweep w-fit">
                  {COMPANY.email}
                </a>
              </div>
              <div className="flex gap-4 items-center">
                <Phone size={24} weight="duotone" className="text-[#0b2046] shrink-0" />
                <a href={`tel:${COMPANY.phone.replace(/\s/g, "")}`} data-testid="contact-phone-link" className="text-[#050a11] link-sweep w-fit">
                  {COMPANY.phone}
                </a>
              </div>
              <div className="pt-6 border-t border-black/10 font-mono-x text-xs text-[#4b5563] tracking-widest">
                {COMPANY.ssm}
              </div>
            </div>
          </div>

          {/* form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="md:col-span-7"
          >
            <form onSubmit={onSubmit} data-testid="contact-form" className="border border-black/10 bg-white p-8 md:p-10 space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="overline text-[#0b2046]/60">Full name</Label>
                  <Input
                    id="name"
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Jane Doe"
                    className="mt-2 rounded-none border-black/15 focus-visible:ring-[#0b2046]"
                  />
                  {errors.name && <p data-testid="contact-error-name" className="mt-1.5 text-xs text-destructive">{errors.name}</p>}
                </div>
                <div>
                  <Label htmlFor="email" className="overline text-[#0b2046]/60">Email</Label>
                  <Input
                    id="email"
                    data-testid="contact-input-email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="jane@brand.com"
                    className="mt-2 rounded-none border-black/15 focus-visible:ring-[#0b2046]"
                  />
                  {errors.email && <p data-testid="contact-error-email" className="mt-1.5 text-xs text-destructive">{errors.email}</p>}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="company" className="overline text-[#0b2046]/60">Company</Label>
                  <Input
                    id="company"
                    data-testid="contact-input-company"
                    value={form.company}
                    onChange={set("company")}
                    placeholder="Brand Ltd."
                    className="mt-2 rounded-none border-black/15 focus-visible:ring-[#0b2046]"
                  />
                </div>
                <div>
                  <Label className="overline text-[#0b2046]/60">Interested in</Label>
                  <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                    <SelectTrigger data-testid="contact-select-service" className="mt-2 rounded-none border-black/15 focus:ring-[#0b2046]">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => (
                        <SelectItem key={s.id} value={s.title} data-testid={`contact-service-${s.id}`}>
                          {s.title}
                        </SelectItem>
                      ))}
                      <SelectItem value="General enquiry" data-testid="contact-service-general">General enquiry</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="overline text-[#0b2046]/60">Message</Label>
                <Textarea
                  id="message"
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={set("message")}
                  rows={5}
                  placeholder="Tell us about your needs…"
                  className="mt-2 rounded-none border-black/15 focus-visible:ring-[#0b2046] resize-none"
                />
                {errors.message && <p data-testid="contact-error-message" className="mt-1.5 text-xs text-destructive">{errors.message}</p>}
              </div>

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="group inline-flex items-center gap-2 bg-[#0b2046] text-white font-medium rounded-full pl-7 pr-5 py-4 hover:bg-[#050a11] active:scale-[0.98] transition-[background-color,transform] disabled:opacity-60"
              >
                {loading ? "Sending…" : "Send message"}
                <ArrowUpRight size={18} weight="bold" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
